import { ReactNode, useMemo, useRef, useState } from 'react';
import {
  NativeSyntheticEvent,
  TextInput,
  TextInputChangeEventData,
  TextInputFocusEventData,
  TextInputKeyPressEventData,
} from 'react-native';
import { CharCounter } from '@/common/components/CharCounter/ChartCounter';
import {
  FloatingField,
  IFloatingFieldProps,
  LabeledInput,
} from '@/common/components/Fields/FloatingField/FloatingField';
import { Icon } from '@/common/components/Icons/Icon';
import { Stack } from '@/common/components/Stack/Stack';

export const TextField = ({
  id,
  type = 'text',
  label,
  value = '',
  valueFormatter,
  inputFormatter,
  error,
  disabled,
  onChange,
  onBlur,
  className,
  rightSibling,
  hint,
  hintHidden,
  maxLength,
  horizontalAlign = 'left',
  inline,
}: IFloatingTextFieldProps) => {
  const [focused, setFocused] = useState(false);
  const keyPressed = useRef<string>('');
  const inputRef = useRef<TextInput | null>(null);

  const handleOnChange = (
    evt: NativeSyntheticEvent<TextInputFocusEventData>
  ) => {
    let { text } = evt.nativeEvent;
    if (maxLength && text.length > maxLength) {
      evt.nativeEvent.text = text.slice(0, maxLength);
    }

    onChange(
      evt,
      inputFormatter
        ? inputFormatter(evt.nativeEvent.text, keyPressed.current)
        : undefined
    );
  };

  const onKeyPress = (
    evt: NativeSyntheticEvent<TextInputKeyPressEventData>
  ) => {
    keyPressed.current = evt.nativeEvent.key;
  };

  const formattedValue = useMemo(() => {
    let finalValue = `${value}`;
    if (maxLength) {
      finalValue = finalValue.slice(0, maxLength);
    }

    if (valueFormatter && finalValue && !focused) {
      return valueFormatter(finalValue);
    }
    return finalValue;
  }, [value, maxLength, valueFormatter, focused]);

  const rightContent = useMemo(() => {
    if (disabled) return <Icon icon="lock" color="neutral20" />;
    if (error) return <Icon icon="circle-exclamation" color="systemError" />;
    if (maxLength && !disabled)
      return (
        <CharCounter value={formattedValue.length} maxLength={maxLength} />
      );
    return rightSibling;
  }, [disabled, error, formattedValue, maxLength, rightSibling]);

  const isDirty = formattedValue.length > 0;

  return (
    <FloatingField
      error={error}
      focused={focused}
      disabled={disabled}
      className={className}
      hint={hint}
      hintHidden={hintHidden}
      inline={inline}
      onPress={() => inputRef.current?.focus()}
    >
      <LabeledInput
        label={label}
        active={focused || isDirty}
        inline={inline}
        ref={inputRef}
        horizontalAlign={horizontalAlign}
        inputProps={{
          id,
          inputMode: type,
          value: formattedValue,
          onChange: handleOnChange,
          onFocus: () => {
            setFocused(true);
          },
          onBlur: (evt) => {
            setFocused(false);
            onBlur?.(evt);
          },
          onKeyPress,
          disabled,
          'aria-invalid': !!error,
        }}
      />
      {rightContent && (
        <Stack horizontal align="center" className="pl-sm">
          {rightContent}
        </Stack>
      )}
    </FloatingField>
  );
};

type FloatingFieldExtendable = Pick<
  IFloatingFieldProps,
  'hint' | 'hintHidden' | 'error' | 'disabled'
>;

export interface IFloatingTextFieldProps extends FloatingFieldExtendable {
  id: string;
  type?: 'text' | 'email' | 'numeric';
  label: string;
  value: string;
  valueFormatter?: (val: string) => string;
  inputFormatter?: (val: string, key: string) => string;
  onChange: (
    evt: NativeSyntheticEvent<TextInputChangeEventData>,
    formatted?: string
  ) => void;
  onBlur?: (evt: NativeSyntheticEvent<TextInputFocusEventData>) => void;
  className?: string;
  maxLength?: number;
  inline?: boolean;
  horizontalAlign?: 'left' | 'center' | 'right';
  rightSibling?: ReactNode;
}
